/**
 * Формулы героя: здоровье, мана, сила автоатаки, мощь, опыт, вклад в рейд.
 *
 * Назначение файла: перенести §Э6.2 в код без отклонений. Эти числа не менялись с v1.3,
 * поэтому файл стабильный — он нужен и бою (packages/server), и предпросмотру в интерфейсе.
 *
 * Импорты: константы HERO из ./constants.
 *
 * Источник: docs/economy/06-formulas.md §Э6.2, docs/07-hero-progression.md
 */

import {
  COMBAT,
  HERO,
  SPELL_COEFFICIENTS,
  SPELL_COOLDOWNS,
  SPELL_EFFECTS,
} from "./constants.js";

/** Проверка уровня: формулы не имеют смысла для нуля и дробей. */
function assertLevel(level: number): void {
  if (!Number.isInteger(level) || level < 1) {
    throw new Error(`Уровень героя должен быть целым от 1, получено ${level}`);
  }
}

/** Здоровье героя на уровне L (Э6.2). Каждый пятый уровень даёт дополнительный запас. */
export function heroHealth(level: number): number {
  assertLevel(level);
  return (
    HERO.healthBase +
    (level - 1) * HERO.healthPerLevel +
    Math.floor(level / 5) * HERO.healthPerFifthLevel
  );
}

/** Мана героя на уровне L (Э6.2). */
export function heroMana(level: number): number {
  assertLevel(level);
  return (
    HERO.manaBase + (level - 1) * HERO.manaPerLevel + Math.floor(level / 5) * HERO.manaPerFifthLevel
  );
}

/** Сила автоатаки на уровне L (Э6.2). */
export function autoAttackPower(level: number): number {
  assertLevel(level);
  return HERO.attackBase + (level - 1) * HERO.attackPerLevel;
}

/**
 * Мощь игрока — число, по которому подбираются противники и считается вклад в рейд (Э6.2).
 * @param spellLevelsSum сумма уровней всех изученных заклинаний
 */
export function playerMight(level: number, spellLevelsSum: number): number {
  assertLevel(level);
  if (spellLevelsSum < 0) throw new Error("Сумма уровней заклинаний не может быть отрицательной");
  return (
    level * HERO.mightPerLevel +
    spellLevelsSum * HERO.mightPerSpellLevel +
    Math.floor(level / 5) * HERO.mightPerFifthLevel
  );
}

/** Накопительный опыт до уровня L (Э6.2). */
export function xpToLevel(level: number): number {
  assertLevel(level);
  return Math.floor(HERO.xpBase * level ** HERO.xpExponent);
}

/**
 * Урон заклинания (Э6.2).
 * @param level уровень героя
 * @param spellCoefficient коэффициент заклинания из книги
 * @param bookUpgrades число улучшений книги: каждое даёт +8%
 */
export function spellDamage(level: number, spellCoefficient: number, bookUpgrades: number): number {
  if (spellCoefficient <= 0) throw new Error("Коэффициент заклинания должен быть положительным");
  if (bookUpgrades < 0) throw new Error("Число улучшений книги не может быть отрицательным");
  return Math.floor(
    autoAttackPower(level) * spellCoefficient * (1 + bookUpgrades * HERO.spellbookBonusPerUpgrade),
  );
}

/**
 * Вклад участника рейда по его номеру в очереди (Э6.2).
 * Затухание 0.55 на каждого следующего: шестой участник почти не усиливает сторону,
 * поэтому численный перевес не решает бой.
 * @param position номер в очереди, начиная с 1
 */
export function raidContribution(might: number, position: number): number {
  if (!Number.isInteger(position) || position < 1) {
    throw new Error(`Номер в очереди рейда должен быть целым от 1, получено ${position}`);
  }
  return might * HERO.raidContributionDecay ** (position - 1);
}

/** Суммарная мощь стороны рейда: участники складываются с затуханием (Э6.2). */
export function sideMight(mights: ReadonlyArray<number>): number {
  return mights.reduce((acc, might, index) => acc + raidContribution(might, index + 1), 0);
}

/**
 * Стойкость цели — доля, на которую снижается входящий урон (Э6.2).
 * Растёт с уровнем почти незаметно: крутая стойкость убила бы рейд слабых на сильного.
 */
export function toughness(level: number): number {
  assertLevel(level);
  return Math.min(
    HERO.toughnessMax,
    HERO.toughnessBase + HERO.toughnessPerLevel * (level - 1),
  );
}

/**
 * Урон, который цель получает на самом деле (Э6.2).
 * Именно эта функция применяется в бою: `spellDamage` даёт исходящий урон,
 * стойкость цели превращает его во входящий.
 */
export function incomingDamage(rawDamage: number, targetLevel: number): number {
  if (rawDamage < 0) throw new Error("Урон не может быть отрицательным");
  return Math.floor(rawDamage * (1 - toughness(targetLevel)));
}

/** Урон автоатаки в секунду: она идёт сама с постоянным интервалом (Э6.2, БОЙ). */
export function autoAttackDamagePerSecond(level: number): number {
  return autoAttackPower(level) / COMBAT.autoAttackIntervalSeconds;
}

/** Сколько здоровья поглощает Щит на данном уровне (Э6.2, ЭФФЕКТЫ). */
export function shieldAbsorb(level: number): number {
  return Math.floor(heroHealth(level) * SPELL_EFFECTS.shieldAbsorbShare);
}

/** Сколько здоровья возвращает Мёд (Э6.2, ЭФФЕКТЫ). */
export function honeyHeal(level: number): number {
  return Math.floor(heroHealth(level) * SPELL_EFFECTS.honeyHealShare);
}

/**
 * Урон в секунду, который сторона из `attackers` бойцов кладёт в одну цель при
 * фокусе огня: автоатака плюс основной жест по перезарядке (Э6.2).
 * Стойкость цели уже учтена — это входящий урон.
 */
export function focusDamagePerSecond(level: number, attackers: number): number {
  assertLevel(level);
  if (attackers < 1) throw new Error("Атакующих не может быть меньше одного");
  const lightningPerSecond =
    (autoAttackPower(level) * SPELL_COEFFICIENTS.lightning) / SPELL_COOLDOWNS.lightningSeconds;
  const raw = attackers * (autoAttackDamagePerSecond(level) + lightningPerSecond);
  return raw * (1 - toughness(level));
}

/**
 * Сколько секунд сторона из `attackers` бойцов снимает сторону из `defenders`
 * при фокусе огня. Нужна инвариантам: закон 4 отводит арене 60-90 секунд, и это
 * единственный способ проверить числа боя, не запуская сервер (Э6.2).
 */
export function estimatedBattleSeconds(level: number, attackers: number, defenders: number): number {
  if (defenders < 1) throw new Error("В бою обе стороны не могут быть пустыми");
  return (defenders * heroHealth(level)) / focusDamagePerSecond(level, attackers);
}

/**
 * Сколько суток нужно, чтобы дойти до уровня при заданном опыте в сутки (Э6.2).
 * Нужна инвариантам темпа прокачки: щит новичка снимается на 8-м уровне, и если
 * дорога туда занимает месяц, щит перестаёт быть щитом новичка (§13.1).
 */
export function daysToLevel(level: number, xpPerDay: number): number {
  assertLevel(level);
  if (xpPerDay <= 0) throw new Error("Опыт в сутки должен быть положительным");
  return xpToLevel(level) / xpPerDay;
}
